<template>
	<Skeleton :paragraph="{ rows: 12 }" :title="false" :loading="loading">
		<Form :model="formState" :label-col="{ span: 12 }" :wrapper-col="{ span: 12 }" :colon="false">
			<FormItem v-bind="validateInfos.radiusVrs">
				<template #label>
					<Tooltip placement="topLeft" color="black">
						<template #title>{{ $t('settings.vrsSettingsForm.radiusVrsHint') }}</template>
						<QuestionCircleOutlined /> {{ $t('settings.vrsSettingsForm.radiusVrs') }}
					</Tooltip>
				</template>
				<div class="flex-wrapper">
					<InputDecimal v-model="formState.radiusVrs" :allowPoint="true" range="positive" class="flex-grow pd-sm-right"/>
					<PopconfirmButton
						:title="$t('settings.vrsSettingsForm.applyRadiusToAllButton.confirmation', { radius: formState.radiusVrs })"
						:ok-text="$t('settings.vrsSettingsForm.applyRadiusToAllButton.ok')"
						:cancel-text="$t('settings.vrsSettingsForm.applyRadiusToAllButton.cancel')"
						:loading="isApplyingRadius"
						:onConfirm="onApplyRadiusToAll"
					>
						{{ $t('settings.vrsSettingsForm.applyRadiusToAllButton.label') }}
					</PopconfirmButton>
				</div>
			</FormItem>
			<FormItem :label="$t('settings.vrsSettingsForm.radiusNear')" v-bind="validateInfos.radiusNear">
				<InputDecimal v-model="formState.radiusNear" :allowPoint="true" range="positive" />
			</FormItem>

			<TypographyParagraph>
				{{ $t('settings.vrsSettingsForm.derivedSettingsHint1') }}
				<TypographyText type="secondary">
					{{ $t('settings.vrsSettingsForm.derivedSettingsHint2') }}
					<router-link :to="{ name: 'settings-type', params: { id: 'sub-networks' } }">{{ $t('settings.vrsSettingsForm.derivedSettingsLink') }}</router-link>
				</TypographyText>
			</TypographyParagraph>

			<FormItem :label="$t('settings.vrsSettingsForm.nameNear')" v-bind="validateInfos.nameNear">
				<InputRestricted v-model="formState.nameNear" :maxlength="SERVICE_NAME_MAX_LENGTH"
					:allowedFormatChecker="mountpointChecker"
					:toolTipText="$t('validators.entrypointNameFormatMessage')" :showTooltipOnFocus="true" />
			</FormItem>
			<FormItem :label="$t('settings.vrsSettingsForm.nameVrs')" v-bind="validateInfos.nameVrs">
				<InputRestricted v-model="formState.nameVrs" :maxlength="SERVICE_NAME_MAX_LENGTH"
					:allowedFormatChecker="mountpointChecker"
					:toolTipText="$t('validators.entrypointNameFormatMessage')" :showTooltipOnFocus="true" />
			</FormItem>
			<FormItem v-if="allowPppRtk" :label="$t('settings.vrsSettingsForm.nameCssr')" v-bind="validateInfos.nameCssr">
				<InputRestricted v-model="formState.nameCssr" :maxlength="SERVICE_NAME_MAX_LENGTH"
					:allowedFormatChecker="mountpointChecker"
					:toolTipText="$t('validators.entrypointNameFormatMessage')" :showTooltipOnFocus="true" />
			</FormItem>
			<FormItem :label="$t('settings.vrsSettingsForm.nameBrdc')" v-bind="validateInfos.nameBrdc">
				<InputRestricted v-model="formState.nameBrdc" :maxlength="SERVICE_NAME_MAX_LENGTH"
					:allowedFormatChecker="mountpointChecker"
					:toolTipText="$t('validators.entrypointNameFormatMessage')" :showTooltipOnFocus="true" />
			</FormItem>
			<FormButtonsItem>
				<Button type="primary" @click.prevent="onSubmit" :loading="isSubmitting">
					{{ $t('settings.vrsSettingsForm.buttonLabel') }}
				</Button>
			</FormButtonsItem>
		</Form>
	</Skeleton>
</template>

<script>
import { reactive, nextTick, watch, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, Skeleton, Form, FormItem, TypographyParagraph, TypographyText, Tooltip, message } from 'ant-design-vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

import Api from '@/api/Api';
import Utils from '@/api/Utils';
import Validators from '@/api/Validators';

import { useVRSSettingsPost } from './useVRSSettingsPost.js'

import { useLicenseStore } from '@/stores/licenseStore';

import InputDecimal from '@/components/common/InputDecimal';
import InputRestricted from '@/components/common/InputRestricted';
import FormButtonsItem from '@/components/common/FormButtonsItem.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';

import { showErrorMessage } from '@/components/common/errorMessage';

import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';

const M_IN_KM = 1000;

const formStateProcessor = formState => {
	let result = Utils.toObject(formState);
	result.radiusVrs = result.radiusVrs * M_IN_KM;
	result.radiusNear = result.radiusNear * M_IN_KM;
	return result;
}

export default {
	components: {
		Button, Skeleton, Form, FormItem, TypographyParagraph, TypographyText, Tooltip,
		InputDecimal, InputRestricted, FormButtonsItem, PopconfirmButton,
		QuestionCircleOutlined,
	},
	props: ['loading', 'settings'],
	setup(props) {
		const { t } = useI18n();
		const formState = reactive({
			radiusVrs: 0,
			radiusNear: 0,
			nameVrs: '',
			nameNear: '',
			nameCssr: '',
			nameBrdc: '',
		});
		const { validate, validateInfos } = Validators.useVRSSettingsDistancesFormRules(formState);
		const { injectValue } = useProvideInjectFormChangedStatuses;
		const formChangedStatuses = injectValue();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(formState, formChangedStatuses, 'DistancesSettingsForm');
		const { isSubmitting, onSubmit } = useVRSSettingsPost(Api.vrsSettingsPost, formState, validate, formStateProcessor, resetChangedState);
		const isApplyingRadius = ref(false);
		const onApplyRadiusToAll = () => {
			isApplyingRadius.value = true;
			const radiusInMeters = formState.radiusVrs * M_IN_KM;
			Api.vrsSettingsApplyRadiusToAll(radiusInMeters, response => {
				isApplyingRadius.value = false;
				if (response.status === 'ok') {
					message.success(t('settings.vrsSettingsForm.applyRadiusToAllButton.success'));
				} else {
					showErrorMessage(response.errors);
				}
			});
		};
		watch(
			() => props.settings,
			value => {
				pauseWatch();
				Utils.updateObject(formState, value);
				formState.radiusVrs = value.radiusVrs / M_IN_KM;
				formState.radiusNear = value.radiusNear / M_IN_KM;
				nextTick(enableWatch);
			},
		);
		const licenseStore = useLicenseStore();
		const allowPppRtk = computed(() => licenseStore.allowPppRtk);
		return {
			isSubmitting,
			isApplyingRadius,
			formState,
			validateInfos,
			onSubmit,
			onApplyRadiusToAll,
			mountpointChecker: Validators.mountpointChecker,
			allowPppRtk,
			SERVICE_NAME_MAX_LENGTH: Validators.SERVICE_NAME_MAX_LENGTH,
		};
	},
}
</script>
